/**
 * BackgroundVideo custom element
 * - Plays/pauses based on viewport visibility
 * - Supports YouTube/Vimeo iframes and native <video> (Shopify-hosted video_tag)
 * - Optional GSAP text animations if available
 */
if (!customElements.get('background-video')) {
  class BackgroundVideo extends HTMLElement {
    constructor() {
      super();

      this._observer = null;
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
      this._onMediaLoaded = this._onMediaLoaded.bind(this);
    }

    connectedCallback() {
      // Cache DOM
      this.videoContainer = this.querySelector('.background-video__iframe');
      this.mediaEl = this.videoContainer?.querySelector('iframe, video') || null;

      // Provider can be on the container (your current markup) or on the element itself
      this.provider =
        this.videoContainer?.dataset.provider ||
        this.dataset.provider ||
        (this.mediaEl?.tagName === 'VIDEO' ? 'html5' : null);

      // Optional: if you add data-force-fallback-mobile="true" in Liquid later
      this.forceFallbackMobile = this.dataset.forceFallbackMobile === 'true';
      this.isMobile = window.matchMedia('(max-width: 749px)').matches;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // If you want to enforce fallback on mobile / reduced motion, disable media here
      if ((this.forceFallbackMobile && this.isMobile) || this.prefersReducedMotion) {
        this._disableMedia();
      } else {
        this._initMedia();
      }

      // Animations (only if libs exist and theme enables them)
      const animationsEnabled = document.body.classList.contains('animations-true');
      const gsapAvailable = typeof gsap !== 'undefined';

      if (animationsEnabled && gsapAvailable) {
        this.prepareAnimations();
      }
    }

    disconnectedCallback() {
      this._teardownObserver();
      document.removeEventListener('visibilitychange', this._onVisibilityChange);

      if (this.mediaEl?.tagName === 'IFRAME') {
        this.mediaEl.removeEventListener('load', this._onMediaLoaded);
      }
    }

    _disableMedia() {
      // Keeps fallback image visible; removes heavy media element
      if (!this.mediaEl) return;

      try {
        this.pause();
      } catch (e) {
        // ignore
      }

      this.mediaEl.remove();
      this.mediaEl = null;
    }

    _initMedia() {
      if (!this.mediaEl) return;

      // If iframe, wait for load (but also set up observer immediately)
      if (this.mediaEl.tagName === 'IFRAME') {
        this.mediaEl.addEventListener('load', this._onMediaLoaded, { once: true });
      }

      // If HTML5 video, can attempt play when visible
      if (this.mediaEl.tagName === 'VIDEO') {
        // Ensure background-style behavior
        this.mediaEl.muted = true;
        this.mediaEl.playsInline = true;
      }

      // Play/pause based on viewport visibility
      this._setupObserver();

      // Pause when tab not visible
      document.addEventListener('visibilitychange', this._onVisibilityChange);
    }

    _onMediaLoaded() {
      // Don’t force autoplay immediately; observer will call play() when visible.
      // But a tiny delay helps postMessage control be accepted by players.
      setTimeout(() => {
        // If already in view, attempt play
        if (this._isInViewportApprox()) this.play();
      }, 150);
    }

    _onVisibilityChange() {
      if (document.hidden) {
        this.pause();
      } else {
        // Only play if element is on screen
        if (this._isInViewportApprox()) this.play();
      }
    }

    _isInViewportApprox() {
      // Lightweight fallback check (observer is the main control)
      const rect = this.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }

    _setupObserver() {
      this._teardownObserver();

      this._observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.target !== this) continue;

            if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
              this.play();
            } else {
              this.pause();
            }
          }
        },
        {
          threshold: [0, 0.25, 0.5, 1],
        }
      );

      this._observer.observe(this);
    }

    _teardownObserver() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    play() {
      if (!this.mediaEl) return;

      // Native video
      if (this.mediaEl.tagName === 'VIDEO') {
        const video = this.mediaEl;
        // Autoplay may still be blocked in edge cases; ignore promise rejection
        video.play().catch(() => {});
        return;
      }

      // Iframe providers (YouTube/Vimeo)
      const iframe = this.mediaEl;
      if (!iframe.contentWindow) return;

      // YouTube requires enablejsapi=1 in the iframe URL
      if (this.provider === 'youtube') {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'mute', args: [] }),
          '*'
        );
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
          '*'
        );
      }

      // Vimeo control is most reliable if iframe URL includes: api=1
      if (this.provider === 'vimeo') {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'setVolume', value: 0 }), '*');
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*');
      }
    }

    pause() {
      if (!this.mediaEl) return;

      if (this.mediaEl.tagName === 'VIDEO') {
        this.mediaEl.pause();
        return;
      }

      const iframe = this.mediaEl;
      if (!iframe.contentWindow) return;

      if (this.provider === 'youtube') {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
          '*'
        );
      }

      if (this.provider === 'vimeo') {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
      }
    }

    prepareAnimations() {
      // Guard: ensure plugins exist if you rely on them
      if (typeof SplitText === 'undefined') return;

      const section = this;

      // If ScrollTrigger isn’t registered, gsap.timeline({scrollTrigger}) can error
      const canUseScrollTrigger = typeof ScrollTrigger !== 'undefined';

      const tl = gsap.timeline(
        canUseScrollTrigger
          ? {
              scrollTrigger: {
                trigger: section,
                start: 'top center',
                end: 'bottom bottom',
              },
            }
          : undefined
      );

      const targets = section.querySelectorAll('h1, h2, h3, p');

      document.fonts.ready.then(() => {
        new SplitText(targets, {
          type: 'lines, words',
          linesClass: 'line-child',
        });

        // Animate headings (any h1/h2/h3)
        const headingLines = section.querySelectorAll('h1 .line-child div, h2 .line-child div, h3 .line-child div');
        if (headingLines.length) {
          const duration = 0.7 + (headingLines.length - 1) * 0.05;
          tl.from(
            headingLines,
            {
              duration,
              yPercent: 100,
              stagger: 0.05,
            },
            0
          );
        }

        // Animate paragraphs
        const pLines = section.querySelectorAll('p .line-child div');
        if (pLines.length) {
          const duration = 0.7 + (pLines.length - 1) * 0.02;
          tl.from(
            pLines,
            {
              duration,
              yPercent: 100,
              stagger: 0.02,
            },
            0
          );
        }

        // Animate your actual button class
        const button = section.querySelector('.button');
        if (button) {
          tl.fromTo(
            button,
            { autoAlpha: 0 },
            { duration: 0.5, autoAlpha: 1 },
            0.3
          );
        }
      });
    }
  }

  customElements.define('background-video', BackgroundVideo);
}